/**
 * Expanded trade-in view, inspector-style detail sheet consistent with the Inventory and Orders
 * pages: identity header with actions, flag chips, notes callout, key facts, Supplier / Origin /
 * Payout / Processing sections, line items (initiated vs received), and a tabbed activity region
 * (history, inbound package, sendback packages, gift cards, escalations, emails).
 *
 * This is a partner-facing subset of reware-admin's trade-in detail, not a copy of it — see
 * fetchTradeins.ts for the fields the query deliberately withholds. The Trove-internal actions
 * (item edits, shipping-cost overrides, unpick/refire sendback, packing slips, the story
 * mutations) are likewise not offered here.
 */
import { useRecordContext } from 'react-admin';
import { Box, Chip, Stack, Typography } from '@mui/material';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';

import { CopyButton, displayNameFor, semanticStateColor } from '@src/components/indexPage';

import TradeinActions from './TradeinActions';
import TradeinActivityTabs from './TradeinActivityTabs';
import {
  OriginSection,
  PayoutSection,
  ProcessingSection,
  SupplierSection,
  TradeinFlags,
  TradeinKeyFacts,
  tradeinTypeLabel,
} from './TradeinDetailSections';
import TradeinItems from './TradeinItems';
import type { ConstantOption, TradeinRecord } from './types';

interface TradeinExpandProps {
  states: ConstantOption[];
  conditions: ConstantOption[];
  sendbackStates: ConstantOption[];
  rejectReasons: ConstantOption[];
  giftcardReasons: ConstantOption[];
}

export const TradeinExpand = ({
  states,
  conditions,
  sendbackStates,
  rejectReasons,
  giftcardReasons,
}: TradeinExpandProps) => {
  const tradein = useRecordContext<TradeinRecord>();
  if (!tradein) return null;

  return (
    <Box sx={{ bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', p: 2.5 }}>
      <Stack spacing={2}>
        {/* Identity header + actions */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontSize: 16, lineHeight: 1.3 }}>
                {`Trade-in ${tradein.uuidPrefix ?? `#${tradein.id}`}`}
              </Typography>
              <Chip size="small" color={semanticStateColor(tradein.state)} label={displayNameFor(states, tradein.state)} />
              <Chip size="small" variant="outlined" label={tradeinTypeLabel(tradein.tradeinStyle)} />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5, color: 'text.secondary' }}>
              <Typography variant="caption">
                ID
                {' '}
                {tradein.id}
                <CopyButton name="Trade-in ID" value={String(tradein.id)} />
              </Typography>
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {`tra-${tradein.uuid}`}
                <CopyButton name="Trade-in UUID" value={`tra-${tradein.uuid}`} />
              </Typography>
              {tradein.externalRefId && (
                <Typography variant="caption">{`Ref ${tradein.externalRefId}`}</Typography>
              )}
            </Stack>
          </Box>
          <TradeinActions tradein={tradein} giftcardReasons={giftcardReasons} />
        </Stack>

        <TradeinFlags tradein={tradein} />

        {tradein.notes && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{ bgcolor: '#FDF6EC', border: '1px solid', borderColor: '#F0DFC8', borderRadius: 1, px: 1.5, py: 1 }}
          >
            <StickyNote2OutlinedIcon sx={{ fontSize: 16, mt: '2px', color: '#9A6A2F' }} />
            <Typography variant="body2" sx={{ color: '#6B4F26' }}>
              {tradein.notes}
            </Typography>
          </Stack>
        )}

        <TradeinKeyFacts tradein={tradein} states={states} />

        {/* Detail sections */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(230px, 1fr))' },
            columnGap: 4,
            rowGap: 2.5,
            alignContent: 'start',
          }}
        >
          <SupplierSection tradein={tradein} />
          <OriginSection tradein={tradein} />
          <PayoutSection tradein={tradein} />
          <ProcessingSection tradein={tradein} sendbackStates={sendbackStates} />
        </Box>

        <TradeinItems tradein={tradein} conditions={conditions} rejectReasons={rejectReasons} />

        <TradeinActivityTabs tradein={tradein} />
      </Stack>
    </Box>
  );
};

export default TradeinExpand;
